import { useEffect, useState } from 'react'
import { AlertCircle, BookOpen, ChefHat, Printer, RefreshCw } from 'lucide-react'
import { useGetProductionPlan, useGetStationRecipes } from '../../hooks/backend/recipes'
import { useGetOrderPredictions } from '../../hooks/backend/dashboardPage'
import { toast } from '../../lib/shadcn/sonner'
import { useGetProductsCached } from '../../hooks/cachedQueries'
import RecipeDialog from './RecipeDialog'
import ProductFormDialog from './ProductFormDialog'
import PrintRecipesDialog from './PrintRecipesDialog'
import type { StationRecipe } from '../../utils/stationRecipesPdf'
import { generateProductionPlanPdf, perUseBatchNote } from '../../utils/productionPlanPdf'
import { formatAmount } from '../../utils/format'
import type { Product } from './ProductTable'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../lib/shadcn/card'
import { Badge } from '../../lib/shadcn/badge'
import { Button } from '../../lib/shadcn/button'
import { Label } from '../../lib/shadcn/label'
import { Switch } from '../../lib/shadcn/switch'
import { Skeleton } from '../../lib/shadcn/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../lib/shadcn/table'
import { cn } from '../../lib/shadcn/utils'
import { HelpText } from '../../components/HelpText'

type BagSize = 'Small' | 'Large' | 'Other'

interface PlanProduct {
  sku: string
  item_name: string
  size: BagSize
  weight_oz: number | null
  open_qty: number
  /** Open order bags already printed (made, being packed); not in to_make. */
  printed_qty?: number
  forecast_qty: number
  forecast_raw?: number
  in_stock: number
  to_make: number
}

interface PlanFlavor {
  label_name: string
  has_recipe: boolean
  base_batch_weight_oz: number | null
  products: PlanProduct[]
  units_to_make: number
  oz_to_make: number
  batches_exact: number | null
  batches: number | null
  batches_by_weight?: number | null
  missing_weight: boolean
  blend_oz: number
  used_in: string[]
  made_from: { label_name: string; pct: number | null }[]
  blend_by_percent?: boolean
  recipe_only?: boolean
  tier?: string | null
  tier_min?: number | null
}

interface PlanIngredient {
  ingredient_id: string
  name: string
  amount: number
  unit: string | null
  flavors: string[]
}

interface ProductionPlan {
  includeForecast: boolean
  forecast: { weekStart: string; weekEnd: string; generatedAt: string } | null
  /** The saved forecast's week has already started, so it's left out. */
  forecastStale?: boolean
  flavors: PlanFlavor[]
  ingredients: PlanIngredient[]
  tierMinimumsError?: string | null
}

const SIZES: BagSize[] = ['Small', 'Large', 'Other']

function formatDay(value: string): string {
  const d = new Date(`${value}T00:00:00`)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function num(n: number, digits = 1): string {
  return n.toLocaleString('en-US', { maximumFractionDigits: digits })
}

function weight(oz: number): string {
  return oz >= 16 ? `${num(oz / 16)} lb` : `${num(oz)} oz`
}

function forecastNote(p: PlanProduct): string | null {
  const raw = p.forecast_raw ?? p.forecast_qty
  if (raw === p.forecast_qty) return null
  return p.forecast_qty === 0
    ? `forecast of ${raw} dropped, under half the tier minimum`
    : `forecast of ${raw} raised to meet the tier minimum`
}

function breakdown(p: PlanProduct): string {
  const parts = [`${p.open_qty} on open orders not printed yet`]
  if ((p.printed_qty ?? 0) > 0) parts.push(`${p.printed_qty} already printed (made, not counted)`)
  if (p.forecast_qty > 0) parts.push(`${p.forecast_qty} forecast`)
  const note = forecastNote(p)
  if (note) parts.push(note)
  if (p.in_stock > 0) parts.push(`${p.in_stock} in stock`)
  return parts.join(', ')
}

function SizeCell({ product }: { product: PlanProduct | undefined }) {
  if (!product) return <span className="text-muted-foreground">—</span>
  return (
    <div title={breakdown(product)}>
      <div className="tabular-nums font-medium">{product.to_make}</div>
      <div className="text-xs text-muted-foreground tabular-nums">
        {product.open_qty} ordered
        {product.forecast_qty > 0 && ` + ${product.forecast_qty}`}
        {product.in_stock > 0 && ` − ${product.in_stock}`}
      </div>
      {(product.printed_qty ?? 0) > 0 && (
        <div className="text-xs text-muted-foreground tabular-nums">
          {product.printed_qty} printed
        </div>
      )}
      {forecastNote(product) && (
        <div className="text-xs text-muted-foreground">
          {(product.forecast_raw ?? 0) > product.forecast_qty
            ? `forecast ${product.forecast_raw} dropped`
            : `forecast ${product.forecast_raw} → ${product.forecast_qty}`}
        </div>
      )}
    </div>
  )
}

export default function ProductionPanel() {
  const getPlan = useGetProductionPlan()
  const getPredictions = useGetOrderPredictions()
  const [includeForecast, setIncludeForecast] = useState(true)
  const getProducts = useGetProductsCached()
  const [recipeProduct, setRecipeProduct] = useState<Product | null>(null)
  const [recipeFlavor, setRecipeFlavor] = useState<string | null>(null)
  const [recipeOpen, setRecipeOpen] = useState(false)
  // A product's recipe opens on the Recipe tab of its product dialog.
  const [productOpen, setProductOpen] = useState(false)
  const getStationRecipes = useGetStationRecipes()
  const [printRecipesOpen, setPrintRecipesOpen] = useState(false)

  // Opens the Recipe dialog for a flavor through one of its products, or on
  // its own for a recipe-only flavor (no products).
  const openRecipe = async (flavor: PlanFlavor) => {
    if (flavor.recipe_only) {
      setRecipeProduct(null)
      setRecipeFlavor(flavor.label_name)
      setRecipeOpen(true)
      return
    }
    const skus = new Set(flavor.products.map(p => p.sku))
    // A flavor only made for blends has no products in the plan, so fall back
    // to any product with that label.
    const matches = (p: Product) =>
      skus.has(p.sku) || (p.label_name ?? '').trim() === flavor.label_name
    let all = (getProducts.data as Product[] | null | undefined) ?? []
    if (!all.some(matches)) {
      all = ((await getProducts.trigger()) as Product[] | undefined) ?? []
    }
    const product = all.find(p => skus.has(p.sku)) ?? all.find(matches)
    if (product) {
      setRecipeProduct(product)
      setProductOpen(true)
      return
    }
    setRecipeProduct(null)
    setRecipeFlavor(flavor.label_name)
    setRecipeOpen(true)
  }

  const refreshAfterSave = () => {
    void getPlan.trigger({ includeForecast }, { skipCache: true })
    void getStationRecipes.trigger({}, { skipCache: true })
  }

  // Makes a new forecast for the coming week (same as Regenerate on the
  // Dashboard), then reloads the plan with it.
  const regenerateForecast = async () => {
    try {
      const res = (await getPredictions.trigger({})) as { weekStart?: string; weekEnd?: string } | undefined
      toast.success(
        res?.weekStart && res.weekEnd
          ? `Forecast updated for ${formatDay(res.weekStart)} – ${formatDay(res.weekEnd)}`
          : 'Forecast updated',
      )
      await getPlan.trigger({ includeForecast }, { skipCache: true })
    } catch (error) {
      toast.error(
        `Couldn't regenerate the forecast: ${error instanceof Error ? error.message : 'Unknown error'}`,
      )
    }
  }

  useEffect(() => {
    getPlan.trigger({ includeForecast })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeForecast])

  // Every recipe's station, for the Station line and Print recipes.
  useEffect(() => {
    void getStationRecipes.trigger({})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const plan = getPlan.data as ProductionPlan | null | undefined
  const loading = getPlan.loading && !plan
  const flavors = plan?.flavors ?? []
  const ingredients = plan?.ingredients ?? []
  // Recipe-only flavors (made only to mix into blends) get their own table.
  const productFlavors = flavors.filter(f => !f.recipe_only)
  const recipeOnlyFlavors = flavors.filter(f => f.recipe_only)
  const shownSizes = SIZES.filter(s => productFlavors.some(f => f.products.some(p => p.size === s)))
  const totalBags = flavors.reduce((sum, f) => sum + f.units_to_make, 0)
  // Oz going into blends is already counted in the blend's own weight.
  const totalOz = flavors.reduce((sum, f) => sum + f.oz_to_make - (f.blend_oz ?? 0), 0)
  const noRecipe = flavors.filter(f => !f.has_recipe).length
  const stationRecipes = (getStationRecipes.data as StationRecipe[] | null | undefined) ?? []
  const stationOf = new Map(stationRecipes.map(r => [r.label_name, r.station]))

  const renderRow = (f: PlanFlavor, sizes: BagSize[]) => (
    <TableRow key={f.label_name}>
      <TableCell>
        <div className="font-medium">
          {f.label_name}
        </div>
        {f.has_recipe && stationOf.get(f.label_name) && (
          <div className="text-xs text-muted-foreground">{stationOf.get(f.label_name)}</div>
        )}
        {f.tier && (
          <div className="text-xs text-muted-foreground">
            {f.tier}
            {f.tier_min != null && ` · minimum ${f.tier_min}`}
          </div>
        )}
        {(f.made_from ?? []).length > 0 && (
          <div className="text-xs text-muted-foreground">
            Blend of{' '}
            {f.made_from
              .map(m => (m.pct != null ? `${m.label_name} ${m.pct}%` : m.label_name))
              .join(', ')}
          </div>
        )}
        {(f.blend_oz ?? 0) > 0 && (
          <div className="text-xs text-muted-foreground">
            Includes {weight(f.blend_oz)} for {f.used_in.join(', ')}
          </div>
        )}
      </TableCell>
      {sizes.map(s => (
        <TableCell key={s} className="text-right">
          <SizeCell product={f.products.find(p => p.size === s)} />
        </TableCell>
      ))}
      <TableCell className="text-right tabular-nums">
        {weight(f.oz_to_make)}
        {f.missing_weight && (
          <div className="text-xs text-warning">Missing a bag weight</div>
        )}
      </TableCell>
      <TableCell className="text-right">
        {f.blend_by_percent ? (
          <div
            className="text-xs text-muted-foreground"
            title="A percentage blend: mix the flavors it's made from, no batches of its own"
          >
            Mix only
          </div>
        ) : f.batches != null ? (
          <div
            title={
              perUseBatchNote(f) ??
              `${num(f.batches_exact ?? 0, 2)} batches of ${weight(f.base_batch_weight_oz ?? 0)}`
            }
          >
            <div className="tabular-nums font-medium">{f.batches}</div>
            <div className="text-xs text-muted-foreground">
              of {weight(f.base_batch_weight_oz ?? 0)}
            </div>
            {perUseBatchNote(f) && (
              <div className="text-xs text-muted-foreground">1 per flavor it goes in</div>
            )}
          </div>
        ) : f.has_recipe ? (
          <Badge variant="warning">No batch weight</Badge>
        ) : (
          <button
            type="button"
            onClick={() => void openRecipe(f)}
            title={`Add a recipe for ${f.label_name}`}
          >
            <Badge variant="outline" className="cursor-pointer hover:bg-muted">
              No recipe
            </Badge>
          </button>
        )}
      </TableCell>
    </TableRow>
  )

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-4">
          <div className="space-y-1.5">
            <CardTitle className="text-base flex items-center gap-2">
              <ChefHat className="w-4 h-4" />
              Production plan
            </CardTitle>
            <CardDescription><HelpText>
              Bags to make for open orders that haven&apos;t been printed yet (printed labels
              were already made and are being packed)
              {includeForecast && plan?.forecast && !plan.forecastStale && (
                <>
                  {' '}plus next week&apos;s forecast ({formatDay(plan.forecast.weekStart)} –{' '}
                  {formatDay(plan.forecast.weekEnd)})
                </>
              )}
              , minus what&apos;s in stock. A flavor&apos;s forecast (Small and Large together) below
              its wholesale tier minimum is raised to the minimum when it&apos;s at least half of it,
              and dropped otherwise. Flavors without a tier minimum are left out when nothing is
              ordered and the forecast is under 6. Weight uses each product&apos;s actual bag
              weight when one is recorded. Batches round up to whole recipe batches, with at least
              one batch for each blend a flavor goes into plus one if it&apos;s also sold on its own.
              Blends add their weight to the flavors they&apos;re mixed from.
            </HelpText></CardDescription>
          </div>
          <div className="flex flex-wrap gap-2 sm:shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setPrintRecipesOpen(true)
                void getStationRecipes.trigger({}, { skipCache: true })
              }}
              title="Print recipes grouped by station (Tumbler, Caramelizer, Kettle)"
            >
              <BookOpen className="w-4 h-4 mr-2" />
              Print recipes
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => plan && generateProductionPlanPdf(plan)}
              disabled={!plan}
              title="Open a printer-friendly PDF of this plan"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print PDF
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => getPlan.trigger({ includeForecast }, { skipCache: true })}
              disabled={getPlan.loading}
            >
              <RefreshCw className={cn('w-4 h-4 mr-2', getPlan.loading && 'animate-spin')} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <div className="flex items-center gap-2">
              <Switch
                id="production-forecast"
                checked={includeForecast}
                onCheckedChange={setIncludeForecast}
              />
              <Label htmlFor="production-forecast" className="cursor-pointer font-normal">
                Include next week&apos;s forecast
              </Label>
            </div>
            {plan && (
              <p className="text-sm text-muted-foreground">
                {num(totalBags, 0)} bags · {weight(totalOz)} of popcorn · {productFlavors.length}{' '}
                flavors
                {recipeOnlyFlavors.length > 0 && ` + ${recipeOnlyFlavors.length} recipe-only`}
              </p>
            )}
          </div>
          {includeForecast && plan && !plan.forecast && (
            <p className="text-sm text-muted-foreground">
              No forecast yet. Generate one on the Dashboard to include it here.
            </p>
          )}
          {includeForecast && plan?.forecast && plan.forecastStale && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
              <span>
                The saved forecast is for {formatDay(plan.forecast.weekStart)} –{' '}
                {formatDay(plan.forecast.weekEnd)}, which has already started, so it isn&apos;t
                included. Regenerate it to plan for the coming week.
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => void regenerateForecast()}
                disabled={getPredictions.loading}
                className="shrink-0"
              >
                <RefreshCw className={cn('w-4 h-4 mr-2', getPredictions.loading && 'animate-spin')} />
                {getPredictions.loading ? 'Regenerating…' : 'Regenerate forecast'}
              </Button>
            </div>
          )}
          {includeForecast && plan?.tierMinimumsError && (
            <p className="text-sm text-muted-foreground">
              Couldn&apos;t read tier minimums from B2B, so the minimums in Settings were used:{' '}
              {plan.tierMinimumsError}
            </p>
          )}
          {noRecipe > 0 && (
            <p className="text-sm text-muted-foreground">
              {noRecipe} of {flavors.length} flavors have no recipe yet, so they show weight but no
              batches or ingredients. Click No recipe to add one.
            </p>
          )}

          {getPlan.error && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {getPlan.error}
            </div>
          )}

          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : flavors.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              Nothing to make right now.
            </p>
          ) : (
            productFlavors.length > 0 && (
            <div className="rounded-md border overflow-x-auto">
              <Table data-mobile-layout="grid">
                <TableHeader>
                  <TableRow>
                    <TableHead>Flavor</TableHead>
                    {shownSizes.map(s => (
                      <TableHead key={s} className="text-right" data-mobile="key">
                        {s === 'Other' ? 'Other bags' : s}
                      </TableHead>
                    ))}
                    <TableHead className="text-right" data-mobile="key">Weight</TableHead>
                    <TableHead className="text-right" data-mobile="key">Batches</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {productFlavors.map(f => renderRow(f, shownSizes))}
                </TableBody>
              </Table>
            </div>
            )
          )}
        </CardContent>
      </Card>

      {!loading && recipeOnlyFlavors.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recipe-only flavors</CardTitle>
            <CardDescription><HelpText>
              Flavors that aren&apos;t sold on their own, made only to mix into the blends above.
              Their weight is already counted in the blend&apos;s bags.
            </HelpText></CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border overflow-x-auto">
              <Table data-mobile-layout="grid">
                <TableHeader>
                  <TableRow>
                    <TableHead>Flavor</TableHead>
                    <TableHead className="text-right" data-mobile="key">Weight</TableHead>
                    <TableHead className="text-right" data-mobile="key">Batches</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>{recipeOnlyFlavors.map(f => renderRow(f, []))}</TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Ingredients needed</CardTitle>
          <CardDescription><HelpText>
            For the whole batches above, from each flavor&apos;s recipe. Amounts are in the
            ingredient&apos;s cost unit when the recipe unit converts to it.
          </HelpText></CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className="h-24 w-full" />
          ) : ingredients.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No recipes for the flavors in this plan yet.
            </p>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ingredient</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Used in</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ingredients.map(i => (
                    <TableRow key={`${i.ingredient_id}-${i.unit ?? ''}`}>
                      <TableCell className="font-medium">{i.name}</TableCell>
                      <TableCell className="text-right tabular-nums whitespace-nowrap">
                        <span title={`${num(i.amount, 2)} ${i.unit ?? ''}`.trim()}>
                          {formatAmount(i.amount, i.unit)}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {i.flavors.join(', ')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <RecipeDialog
        product={null}
        flavorName={recipeFlavor}
        open={recipeOpen}
        onOpenChange={setRecipeOpen}
        onSaved={refreshAfterSave}
      />

      <ProductFormDialog
        product={recipeProduct}
        initialTab="recipe"
        open={productOpen && !!recipeProduct}
        onOpenChange={setProductOpen}
        onSaved={refreshAfterSave}
      />

      <PrintRecipesDialog
        open={printRecipesOpen}
        onOpenChange={setPrintRecipesOpen}
        recipes={stationRecipes}
        plan={plan ?? null}
        loading={getStationRecipes.loading && stationRecipes.length === 0}
      />
    </div>
  )
}
